import React from "react";
import { Box, Button, Typography } from "@mui/material";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // A stale tab can still be holding a bundle that references chunk hashes a
  // newer deployment has since deleted from the server (404 on the .chunk.js
  // file). Reloading once picks up the new build transparently. Guarded by
  // sessionStorage so a *genuinely* broken/missing chunk doesn't reload-loop
  // forever — it falls through to the manual refresh UI after one attempt.
  isChunkLoadError(error) {
    return (
      error?.name === "ChunkLoadError" ||
      /Loading (chunk|CSS chunk) [\w-]+ failed/i.test(error?.message || "")
    );
  }

  componentDidCatch(error, info) {
    console.error("Uncaught render error:", error, info);

    if (this.isChunkLoadError(error)) {
      const alreadyReloaded = sessionStorage.getItem("chunkReloadAttempted");
      if (!alreadyReloaded) {
        sessionStorage.setItem("chunkReloadAttempted", "true");
        window.location.reload();
        return;
      }
    }
    sessionStorage.removeItem("chunkReloadAttempted");
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100vh"
          gap={2}
          textAlign="center"
          px={2}
        >
          <Typography variant="h6">Something went wrong.</Typography>
          <Typography variant="body2" color="text.secondary">
            Please refresh the page and try again.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Refresh
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
